import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { KindPill } from '../shared/pills'
import type { CommentKind } from '../store'
import { useCoachingStore } from '../store'
import type { ReviewComment } from './contracts'
import { DiffBlock, type DiffMode } from './diff-block'

/**
 * Kind palette (spec §8.2), card-level colors + the pill colors F1's
 * `KindPill` doesn't expose. Exported so Composer/OverallComment's kind
 * switcher pills (which need active/inactive coloring, not just a static
 * display pill) share one source of truth instead of re-deriving it.
 */
export const KIND_ACCENT: Record<
  CommentKind,
  { pillBg: string; pillFg: string; cardBg: string; cardBorder: string }
> = {
  praise: {
    pillBg: 'color-mix(in oklab, var(--cd-green) 14%, transparent)',
    pillFg: 'var(--cd-green)',
    cardBg: 'var(--cd-card-green)',
    cardBorder: 'color-mix(in oklab, var(--cd-green) 22%, transparent)',
  },
  critique: {
    pillBg: 'color-mix(in oklab, var(--cd-red) 14%, transparent)',
    pillFg: 'var(--cd-red)',
    cardBg: 'var(--cd-card-red)',
    cardBorder: 'color-mix(in oklab, var(--cd-red) 22%, transparent)',
  },
  suggestion: {
    pillBg: 'color-mix(in oklab, var(--cd-purple) 14%, transparent)',
    pillFg: 'var(--cd-purple)',
    cardBg: 'var(--cd-card-purple)',
    cardBorder: 'color-mix(in oklab, var(--cd-purple) 22%, transparent)',
  },
}

// Right-aligned status text (spec §8.2). AI attribution never disappears —
// note there's no "AI · dismissed" case because dismissed comments are
// coach-side only and the caller (commentsFor) filters them out before they
// ever reach a CommentCard. The AI pending case never reaches a card from a
// coding surface (LG-632): the live mapper suppresses pending AI rows on
// coding reviews and the coding seeds carry none. Behavioral/system-design
// pending drafts still hit it, with the approve/rewrite/dismiss lifecycle
// below intact.
//
// A coach comment says whether the client can read it (LG-641) — "just now"
// told the coach when they wrote it, which is the one thing they already
// knew, and said nothing about the thing they could not see.
function statusText(comment: ReviewComment): string {
  if (comment.source === 'coach') {
    return comment.status === 'posted' ? 'You · published' : 'You · draft'
  }
  if (comment.status === 'pending') return 'AI · pending approval'
  return comment.status === 'posted' ? 'AI · posted' : 'AI · approved'
}

/**
 * Height-relevant local state a CommentCard reports up via `onLayoutChange`
 * so the gutter engine's estimate can apply spec §8.1's +170/+130 (open
 * diff, inline/side) and +100 (rewrite panel) bumps. `openDiff` is null
 * when the comment has no diff; the rewrite panel only exists while the
 * comment is pending, so `rewriteOpen` reports false once it's approved.
 */
export interface CommentCardLayout {
  openDiff: DiffMode | null
  rewriteOpen: boolean
}

export function CommentCard(props: {
  comment: ReviewComment
  style?: CSSProperties
  /** Called (post-render) whenever diff-mode/rewrite-panel state changes,
   * including once on mount. Callers keeping the result in state should
   * bail out of no-op updates — ReviewTab's handler does. */
  onLayoutChange?: (id: string, layout: CommentCardLayout) => void
}): ReactNode {
  const { comment, style, onLayoutChange } = props
  const { approve, dismiss, rewrite, editBody, markPosted, saveEdit } =
    useCoachingStore().review
  const [rewriteOpen, setRewriteOpen] = useState(false)
  const [rewriteNotes, setRewriteNotes] = useState('')
  const [diffMode, setDiffMode] = useState<DiffMode>('inline')
  // The save stays on screen for the whole round trip (the draft is only
  // dropped once the saved text comes back), so the button says what it is
  // doing instead of inviting a second, version-stale attempt.
  const [saving, setSaving] = useState(false)

  const accent = KIND_ACCENT[comment.kind]
  // The approve/rewrite/dismiss row belongs to AI drafts only. A coach
  // comment is also `pending` while it is unpublished (LG-641), and approving
  // it would be a second way to release feedback — the one thing the publish
  // control exists to be sole owner of.
  const isAiDraft = comment.status === 'pending' && comment.source === 'ai'
  const isApproved = comment.status === 'approved'
  const hasDiff = comment.diff != null

  useEffect(() => {
    onLayoutChange?.(comment.id, {
      openDiff: hasDiff ? diffMode : null,
      rewriteOpen: isAiDraft && rewriteOpen,
    })
  }, [comment.id, hasDiff, diffMode, isAiDraft, rewriteOpen, onLayoutChange])

  return (
    <div
      style={{
        ...style,
        background: accent.cardBg,
        borderColor: accent.cardBorder,
      }}
      className="w-[308px] rounded-xl border p-3.5 shadow-[0_6px_24px_rgba(0,0,0,.35)]"
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
        <KindPill kind={comment.kind} ai={comment.source === 'ai'} />
        <span
          className="rounded-full border border-(--cd-border-2) px-2 py-0.5 text-[10.5px] font-semibold text-(--cd-text-muted)"
          style={{
            background: 'color-mix(in oklab, var(--cd-bg) 50%, transparent)',
          }}
        >
          {comment.target}
        </span>
        <span className="ml-auto text-[10px] text-(--cd-text-faint)">
          {statusText(comment)}
        </span>
      </div>

      <textarea
        value={comment.body}
        onChange={(e) => editBody(comment.id, e.target.value)}
        // Held for the round trip: the save publishes the wording it captured
        // on click, and the refresh that follows drops the local draft — so
        // anything typed in between would be silently discarded.
        readOnly={saving}
        rows={3}
        className="w-full resize-none rounded-md border border-transparent bg-transparent px-1.5 py-1 text-[12.5px] leading-[1.55] text-(--cd-text-body) outline-none focus:border-(--cd-border-4) focus:bg-black/20"
      />

      {comment.diff && (
        <DiffBlock diff={comment.diff} mode={diffMode} onToggle={setDiffMode} />
      )}

      {/* Typed-over text is local until it is saved. Saving writes a
          `coach_edit` revision and publishes it in one transaction, so a
          comment the client is already reading is corrected with its history
          intact (LG-641). `edited` is only set where the service will accept
          the save, so this never offers an action that comes back refused. */}
      {comment.edited === true && (
        <div className="mt-2.5 flex items-center gap-1.5">
          <button
            type="button"
            disabled={saving}
            onClick={async () => {
              setSaving(true)
              try {
                await saveEdit(comment.id)
              } finally {
                setSaving(false)
              }
            }}
            className="rounded-md bg-(--cd-text) px-3 py-1 text-[11.5px] font-semibold text-(--cd-bg) disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save edit'}
          </button>
          <span className="text-[10.5px] text-(--cd-text-faint)">
            {comment.status === 'pending'
              ? 'Unsaved — this draft keeps its stored wording until you save.'
              : 'Unsaved — the client still reads the previous wording.'}
          </span>
        </div>
      )}

      {/* Approved → posted (spec §8.2): the per-comment sign-off. "Mark", not
          "Post" — approved comments are already client-visible, so the verb
          must not promise a visibility change. Only AI comments ever reach
          `approved`; a coach one is released by the review's publish control
          (LG-641), never by approval. */}
      {isApproved && (
        <div className="mt-2.5 flex gap-1.5">
          <button
            type="button"
            // Held with the textarea: this command carries the stored body and
            // drops the local draft, which mid-save would wipe the wording the
            // in-flight write is still trying to save.
            disabled={saving}
            onClick={() => markPosted(comment.id)}
            className="rounded-md bg-(--cd-text) px-3 py-1 text-[11.5px] font-semibold text-(--cd-bg) disabled:opacity-50"
          >
            → Mark posted
          </button>
        </div>
      )}

      {isAiDraft && (
        <>
          <div className="mt-2.5 flex gap-1.5">
            <button
              type="button"
              onClick={() => approve(comment.id)}
              className="rounded-md bg-(--cd-text) px-3 py-1 text-[11.5px] font-semibold text-(--cd-bg)"
            >
              ✓ Approve
            </button>
            <button
              type="button"
              onClick={() => setRewriteOpen((v) => !v)}
              className="rounded-md border px-3 py-1 text-[11.5px] font-medium text-(--cd-purple)"
              style={{
                borderColor:
                  'color-mix(in oklab, var(--cd-purple) 30%, transparent)',
              }}
            >
              ✦ Rewrite…
            </button>
            <button
              type="button"
              onClick={() => dismiss(comment.id)}
              className="rounded-md border border-transparent px-2.5 py-1 text-[11.5px] font-medium text-(--cd-text-muted) hover:text-(--cd-red)"
            >
              Dismiss
            </button>
          </div>

          {rewriteOpen && (
            <div
              className="mt-2 rounded-lg border border-(--cd-border-2) p-2.5"
              style={{
                background:
                  'color-mix(in oklab, var(--cd-bg) 50%, transparent)',
              }}
            >
              <textarea
                value={rewriteNotes}
                onChange={(e) => setRewriteNotes(e.target.value)}
                placeholder="Notes for the AI — e.g. softer tone, mention the invariant by name…"
                rows={2}
                className="w-full resize-none border-none bg-transparent text-xs leading-snug text-(--cd-text-body) outline-none"
              />
              <div className="mt-1.5 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    rewrite(comment.id)
                    setRewriteOpen(false)
                    setRewriteNotes('')
                  }}
                  className="rounded-md bg-(--cd-purple) px-3 py-1 text-[11px] font-semibold text-(--cd-bg)"
                >
                  Rewrite
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
